import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  Container,
  Header,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
} from '@amzn/awsui-components-react';
import {
  BulkCreateSignupSingleShiftResponse,
  IdentifierType,
  Shift,
  Person,
} from '@amzn/red-velvet-api';
import { BulkSignupResult, toBulkSignupResult } from '../../utils/signupUtils';
import { getApiErrorMessage } from '../../utils/errorHelper';
import { useTranslation } from 'react-i18next';
import { useBulkCreateSignup } from '../../hooks/signup/useBulkCreateSignup';
import { useRefreshSignupCapacity } from '../../hooks/signup/useRefreshSignupCapacity';
import { publishKatalMetric } from '../katalAnalytics';
import {
  useInvalidSignupColumns,
  useValidSignupColumns,
  useAlreadySignedUpColumns,
  FailedSignupIdentifier,
  ValidSignupUser,
  AlreadySignedUpUser,
} from './signupValidationColumns';

const DEFAULT_PAGE_SIZE = 100;

enum Tab {
  INVALID = 'invalid',
  VALID = 'valid',
  SKIPPED = 'skipped',
}

interface ValidateSignupsContentProps {
  validationData: BulkCreateSignupSingleShiftResponse;
  identifierType: IdentifierType;
  shift: Shift;
  eventId: string;
  remainingSpots: number;
  onCancel: () => void;
  onPrevious: () => void;
  onComplete: (result?: BulkSignupResult) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  metricKeys: { addSignupFromBulkMetricKey: string };
}

function personToValidUser(person: Person, shiftName: string): ValidSignupUser {
  return {
    name: `${person.lastName || ''}, ${person.firstName || ''}`.trim(),
    alias: person.alias || '',
    shiftName,
  };
}

function personToAlreadySignedUp(person: Person, shiftName: string): AlreadySignedUpUser {
  return {
    name: `${person.lastName || ''}, ${person.firstName || ''}`.trim(),
    alias: person.alias || '',
    shiftName,
  };
}

export function ValidateSignupsContent({
  validationData,
  identifierType,
  shift,
  eventId,
  remainingSpots,
  onCancel,
  onPrevious,
  onComplete,
  onSubmittingChange,
  metricKeys,
}: ValidateSignupsContentProps) {
  const { t } = useTranslation('signup');
  const bulkCreateSignup = useBulkCreateSignup(eventId);
  const refreshSignupCapacity = useRefreshSignupCapacity(eventId);
  const shiftName = shift.name;
  const shiftId = shift.shiftId;

  const getErrorMessage = useCallback(
    (type: IdentifierType): string => {
      switch (type) {
        case IdentifierType.ALIAS:
          return t('bulkSignupModal.validation.errors.noEmployeeAlias');
        case IdentifierType.EMPLOYEE_ID:
          return t('bulkSignupModal.validation.errors.noEmployeeId');
        case IdentifierType.BADGE_ID:
          return t('bulkSignupModal.validation.errors.noEmployeeBadgeId');
      }
    },
    [t]
  );

  const initialFailed = useMemo(
    () =>
      validationData.invalidSignupUsers.map((user, index) => ({
        id: index,
        correctedValue: user.identifierValue || '',
        originalValue: user.identifierValue || '',
        error: getErrorMessage(identifierType),
        input: user.identifierValue || '',
        isEditing: false,
        isResolved: false,
        isValidating: false,
      })),
    [validationData.invalidSignupUsers, identifierType, getErrorMessage]
  );

  const [failedIdentifiers, setFailedIdentifiers] =
    useState<FailedSignupIdentifier[]>(initialFailed);
  const [resolvedValidUsers, setResolvedValidUsers] = useState<ValidSignupUser[]>([]);
  const [resolvedAlreadySignedUp, setResolvedAlreadySignedUp] = useState<AlreadySignedUpUser[]>([]);
  const [activeTabId, setActiveTabId] = useState<Tab>(
    initialFailed.length > 0 ? Tab.INVALID : Tab.VALID
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const validUsers = useMemo(() => {
    const originalAliases = new Set(validationData.validSignupUsers.map((p) => p.alias));
    const alreadySignedUpAliases = new Set(validationData.alreadySignedUpUsers.map((p) => p.alias));
    const deduped = resolvedValidUsers.filter(
      (u) => !originalAliases.has(u.alias) && !alreadySignedUpAliases.has(u.alias)
    );
    return [
      ...validationData.validSignupUsers.map((p) => personToValidUser(p, shiftName)),
      ...deduped,
    ];
  }, [
    validationData.validSignupUsers,
    validationData.alreadySignedUpUsers,
    resolvedValidUsers,
    shiftName,
  ]);

  const alreadySignedUpUsers = useMemo(() => {
    const originalAliases = new Set(validationData.alreadySignedUpUsers.map((p) => p.alias));
    const deduped = resolvedAlreadySignedUp.filter((u) => !originalAliases.has(u.alias));
    return [
      ...validationData.alreadySignedUpUsers.map((p) => personToAlreadySignedUp(p, shiftName)),
      ...deduped,
    ];
  }, [validationData.alreadySignedUpUsers, resolvedAlreadySignedUp, shiftName]);

  const invalidCount = failedIdentifiers.filter((i) => !i.isResolved).length;
  const validCount = validUsers.length;
  const skippedCount = alreadySignedUpUsers.length;

  // Pagination
  const paginateItems = useCallback(
    <T,>(items: T[]): T[] => {
      const start = (currentPageIndex - 1) * pageSize;
      return items.slice(start, start + pageSize);
    },
    [currentPageIndex, pageSize]
  );

  const getTotalPages = useCallback((): number => {
    let total = 0;
    switch (activeTabId) {
      case Tab.INVALID:
        total = failedIdentifiers.length;
        break;
      case Tab.VALID:
        total = validUsers.length;
        break;
      case Tab.SKIPPED:
        total = alreadySignedUpUsers.length;
        break;
    }
    return Math.ceil(total / pageSize);
  }, [
    activeTabId,
    failedIdentifiers.length,
    validUsers.length,
    alreadySignedUpUsers.length,
    pageSize,
  ]);

  // Inline correction handlers
  const handleCorrectedValueChange = (id: number, value: string) => {
    setFailedIdentifiers((prev) =>
      prev.map((item) => (item.id === id ? { ...item, correctedValue: value } : item))
    );
  };

  const handleEditClick = (id: number) => {
    setFailedIdentifiers((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isEditing: true, originalValue: item.correctedValue } : item
      )
    );
  };

  const handleCancelEdit = (id: number) => {
    setFailedIdentifiers((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isEditing: false, correctedValue: item.originalValue } : item
      )
    );
  };

  const handleAcceptCorrection = async (id: number) => {
    const item = failedIdentifiers.find((i) => i.id === id);
    if (!item || item.isResolved) return;

    const correctedValue = item.correctedValue;
    setFailedIdentifiers((prev) =>
      prev.map((i) => (i.id === id ? { ...i, isValidating: true } : i))
    );

    try {
      const response = await bulkCreateSignup.mutateAsync({
        validationOnly: true,
        shiftSignupUsersMap: {
          [shiftId]: [{ identifierType, identifierValue: correctedValue }],
        },
      });

      const shiftResponse = response.shiftSignupResponseMap[shiftId];

      if (shiftResponse.invalidSignupUsers.length === 0) {
        // Correction succeeded — mark resolved and add to appropriate list
        setFailedIdentifiers((prev) =>
          prev.map((i) =>
            i.id === id
              ? {
                  ...i,
                  isEditing: false,
                  isResolved: true,
                  isValidating: false,
                  error: t('bulkSignupModal.validation.resolved'),
                }
              : i
          )
        );
        setAlertMessage(null);

        shiftResponse.validSignupUsers.forEach((p) => {
          const user = personToValidUser(p, shiftName);
          setResolvedValidUsers((prev) =>
            prev.some((u) => u.alias === user.alias) ? prev : [...prev, user]
          );
        });
        shiftResponse.alreadySignedUpUsers.forEach((p) => {
          const user = personToAlreadySignedUp(p, shiftName);
          setResolvedAlreadySignedUp((prev) =>
            prev.some((u) => u.alias === user.alias) ? prev : [...prev, user]
          );
        });
      } else {
        setAlertMessage(
          t('bulkSignupModal.validation.errors.correctedValueInvalid', { correctedValue })
        );
        setFailedIdentifiers((prev) =>
          prev.map((i) => (i.id === id ? { ...i, isValidating: false, isEditing: false } : i))
        );
      }
    } catch (error) {
      setAlertMessage(getApiErrorMessage(error) ?? t('bulkSignupModal.validation.errors.internal'));
      refreshSignupCapacity();
      setFailedIdentifiers((prev) =>
        prev.map((i) => (i.id === id ? { ...i, isValidating: false, isEditing: false } : i))
      );
    }
  };

  const handleSkipAll = () => {
    setFailedIdentifiers([]);
    setActiveTabId(Tab.VALID);
  };

  const handleAddSignups = async () => {
    setIsSubmitting(true);
    setAlertMessage(null);
    onSubmittingChange?.(true);
    publishKatalMetric(metricKeys.addSignupFromBulkMetricKey, validCount.toString());
    try {
      const signupUsers = validUsers.map((u) => ({
        identifierType: IdentifierType.ALIAS,
        identifierValue: u.alias,
      }));

      const response = await bulkCreateSignup.mutateAsync({
        validationOnly: false,
        shiftSignupUsersMap: { [shiftId]: signupUsers },
      });

      onComplete(toBulkSignupResult(response.shiftSignupResponseMap[shiftId]));
    } catch (error) {
      // Not the "validating correction" copy: no correction is in play on the submit path.
      setAlertMessage(getApiErrorMessage(error) ?? t('bulkSignupModal.errors.failedToAddSignups'));
      // Refresh so remainingSpots reflects reality — it feeds the Add signups button's own guard.
      refreshSignupCapacity();
    } finally {
      setIsSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  const invalidColumns = useInvalidSignupColumns(
    handleCorrectedValueChange,
    handleAcceptCorrection,
    handleCancelEdit,
    handleEditClick
  );
  const validColumns = useValidSignupColumns();
  const skippedColumns = useAlreadySignedUpColumns();

  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: Tab.INVALID,
        label: (
          <StatusIndicator type="error">
            {t('bulkSignupModal.validation.invalidEntries', { count: invalidCount })}
          </StatusIndicator>
        ),
      },
      {
        id: Tab.VALID,
        label: (
          <StatusIndicator type="success">
            {t('bulkSignupModal.validation.validEntries', { count: validCount })}
          </StatusIndicator>
        ),
      },
      {
        id: Tab.SKIPPED,
        label: (
          <StatusIndicator type="stopped">
            {t('bulkSignupModal.validation.skippedAlreadySignedUp', { count: skippedCount })}
          </StatusIndicator>
        ),
      },
    ];
    return allTabs.filter((tab) => tab.id !== Tab.SKIPPED || skippedCount > 0);
  }, [t, invalidCount, validCount, skippedCount]);

  const paginationControls = useMemo(
    () => ({
      pagination: (
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={getTotalPages()}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
        />
      ),
      preferences: (
        <CollectionPreferences
          title={t('bulkSignupModal.pagination.preferencesTitle')}
          confirmLabel={t('bulkSignupModal.pagination.confirmLabel')}
          cancelLabel={t('bulkSignupModal.pagination.cancelLabel')}
          preferences={{ pageSize }}
          pageSizePreference={{
            title: t('bulkSignupModal.pagination.pageSizeTitle'),
            options: [5, 10, 25, 50, 100].map((size) => ({
              value: size,
              label: t('bulkSignupModal.pagination.pageSize', { count: size }),
            })),
          }}
          onConfirm={({ detail }) => {
            setPageSize(detail.pageSize || DEFAULT_PAGE_SIZE);
            setCurrentPageIndex(1);
          }}
        />
      ),
    }),
    [currentPageIndex, pageSize, t, getTotalPages]
  );

  const renderTabContent = () => {
    switch (activeTabId) {
      case Tab.INVALID:
        return (
          <SpaceBetween size="l">
            <Header
              variant="h3"
              actions={
                <Button variant="link" onClick={handleSkipAll}>
                  {t('bulkSignupModal.validation.skipAllButton')}
                </Button>
              }
            >
              {t('bulkSignupModal.validation.issuesHeader', { count: invalidCount })}
            </Header>
            <Table
              columnDefinitions={invalidColumns}
              items={paginateItems(failedIdentifiers)}
              variant="borderless"
              trackBy="id"
              stickyHeader
              pagination={paginationControls.pagination}
              preferences={paginationControls.preferences}
            />
          </SpaceBetween>
        );
      case Tab.VALID:
        return (
          <Table
            columnDefinitions={validColumns}
            header={
              <Header counter={`(${validCount})`} variant="h3">
                {t('bulkSignupModal.validation.tableHeaders.signupsTitle')}
              </Header>
            }
            items={paginateItems(validUsers)}
            variant="borderless"
            trackBy="alias"
            stickyHeader
            pagination={paginationControls.pagination}
            preferences={paginationControls.preferences}
          />
        );
      case Tab.SKIPPED:
        return (
          <Table
            columnDefinitions={skippedColumns}
            header={
              <Header counter={`(${skippedCount})`} variant="h3">
                {t('bulkSignupModal.validation.tableHeaders.signupsTitle')}
              </Header>
            }
            items={paginateItems(alreadySignedUpUsers)}
            variant="borderless"
            trackBy="alias"
            stickyHeader
            pagination={paginationControls.pagination}
            preferences={paginationControls.preferences}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bulk-upload-modal-scrollable">
        <Container
          header={<Header variant="h3">{t('bulkSignupModal.validation.summaryHeader')}</Header>}
        >
          {/* Sits above the tabs rather than inside one: the submit rejection is raised from the
              footer button, which is only clickable once the invalid tab is empty. */}
          {alertMessage && (
            <Box padding={{ bottom: 'l' }}>
              <Alert type="error" dismissible onDismiss={() => setAlertMessage(null)}>
                {alertMessage}
              </Alert>
            </Box>
          )}
          <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => {
              setActiveTabId(detail.activeTabId as Tab);
              setCurrentPageIndex(1);
            }}
            tabs={tabs}
          />
          {renderTabContent()}
        </Container>
      </div>
      <Box padding={{ top: 'l' }}>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel} disabled={isSubmitting}>
              {t('bulkSignupModal.buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={onPrevious} disabled={isSubmitting}>
              {t('bulkSignupModal.buttons.previous')}
            </Button>
            <Button
              variant="primary"
              disabled={invalidCount > 0 || validCount === 0 || validCount > remainingSpots}
              disabledReason={
                validCount > remainingSpots ? t('signupSheet.blockedReasons.SHIFT_FULL') : undefined
              }
              loading={isSubmitting}
              onClick={handleAddSignups}
            >
              {t('bulkSignupModal.buttons.addSignups', { count: validCount })}
            </Button>
          </SpaceBetween>
        </Box>
      </Box>
    </>
  );
}
